import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Video, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import VideoCard from './VideoCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Comprehensive keyword mapping for better video matching
const articleToVideoKeywords = {
  // Stone procedures
  'ureteroscopy': ['ureteroscopy', 'kidney stones', 'laser lithotripsy', 'stone removal'],
  'lithotripsy': ['lithotripsy', 'eswl', 'shockwave', 'kidney stones'],
  'pcnl': ['pcnl', 'percutaneous', 'nephrolithotomy', 'kidney stones'],
  'kidney stone': ['kidney stones', 'stone', 'lithotripsy', 'ureteroscopy', 'pcnl', 'eswl'],
  
  // Prostate
  'bph': ['bph', 'enlarged prostate', 'prostate', 'turp', 'greenlight', 'urolift', 'rezum'],
  'prostate biopsy': ['prostate biopsy', 'biopsy', 'prostate', 'psa'],
  'prostatectomy': ['prostatectomy', 'prostate cancer', 'robotic surgery', 'prostate removal'],
  'prostate cancer': ['prostate cancer', 'prostatectomy', 'radiation', 'psa', 'biopsy'],
  'psa': ['psa', 'prostate', 'screening', 'biopsy'],
  'turp': ['turp', 'prostate', 'bph', 'resection'],
  'greenlight': ['greenlight', 'laser', 'prostate', 'bph'],
  'urolift': ['urolift', 'prostate', 'bph', 'minimally invasive'],
  'rezum': ['rezum', 'steam', 'prostate', 'bph'],
  'holep': ['holep', 'laser', 'prostate', 'enucleation'],
  
  // Bladder
  'bladder cancer': ['bladder cancer', 'turbt', 'bcg', 'cystectomy', 'bladder'],
  'cystoscopy': ['cystoscopy', 'bladder', 'scope', 'diagnostic'],
  'turbt': ['turbt', 'bladder tumor', 'bladder cancer', 'resection'],
  'bcg': ['bcg', 'bladder cancer', 'immunotherapy', 'intravesical'],
  'cystectomy': ['cystectomy', 'bladder removal', 'bladder cancer', 'urinary diversion'],
  'overactive bladder': ['overactive bladder', 'oab', 'incontinence', 'urgency'],
  'incontinence': ['incontinence', 'leakage', 'sling', 'pelvic floor', 'kegel'],
  
  // Kidney
  'kidney cancer': ['kidney cancer', 'renal cell', 'nephrectomy', 'kidney tumor'],
  'nephrectomy': ['nephrectomy', 'kidney removal', 'kidney cancer', 'robotic'],
  'kidney cyst': ['kidney cyst', 'renal cyst', 'kidney'],
  
  // Testicular
  'testicular cancer': ['testicular cancer', 'orchiectomy', 'testicle'],
  'testicular pain': ['testicular pain', 'testicle', 'scrotal', 'epididymitis'],
  'varicocele': ['varicocele', 'testicle', 'scrotal', 'fertility', 'infertility'],
  'hydrocele': ['hydrocele', 'scrotal', 'testicle'],
  'vasectomy': ['vasectomy', 'male contraception', 'sterilization'],
  'epididymitis': ['epididymitis', 'orchitis', 'testicular pain', 'testicle'],
  'orchitis': ['orchitis', 'epididymitis', 'testicular pain', 'testicle'],
  
  // Men's health
  'erectile dysfunction': ['erectile dysfunction', 'ed', 'impotence', 'sexual health'],
  'testosterone': ['testosterone', 'low t', 'hypogonadism', 'trt'],
  'peyronie': ['peyronie', 'penile', 'curvature'],
  'circumcision': ['circumcision', 'foreskin', 'phimosis'],
  'phimosis': ['phimosis', 'circumcision', 'foreskin', 'tight foreskin'],
  'infertility': ['infertility', 'fertility', 'sperm', 'semen', 'varicocele'],
  'semen analysis': ['semen analysis', 'sperm', 'fertility', 'infertility'],
  
  // Hydrocele and Spermatocele
  'hydrocele': ['hydrocele', 'hydrocelectomy', 'scrotal swelling', 'scrotal', 'testicle'],
  'hydrocelectomy': ['hydrocelectomy', 'hydrocele', 'scrotal surgery', 'testicle'],
  'spermatocele': ['spermatocele', 'spermatocelectomy', 'epididymal cyst', 'scrotal lump', 'testicle'],
  'spermatocelectomy': ['spermatocelectomy', 'spermatocele', 'epididymal cyst', 'scrotal surgery'],
  'epididymal cyst': ['epididymal cyst', 'spermatocele', 'spermatocelectomy', 'testicle'],
  
  // Catheters/devices
  'catheter': ['catheter', 'foley', 'self-catheterization', 'urinary retention'],
  'stent': ['stent', 'ureteral stent', 'kidney stones'],
  'nephrostomy': ['nephrostomy', 'kidney drain', 'tube care'],
  
  // Infections
  'uti': ['uti', 'urinary tract infection', 'bladder infection'],
  'prostatitis': ['prostatitis', 'prostate infection', 'pelvic pain'],
  
  // Urethral
  'stricture': ['stricture', 'urethroplasty', 'urethral'],
  'urethroplasty': ['urethroplasty', 'stricture', 'urethral reconstruction'],
  
  // Upper tract
  'utuc': ['utuc', 'upper tract', 'ureteral cancer', 'renal pelvic'],
  'ureteral cancer': ['ureteral cancer', 'utuc', 'upper tract', 'nephroureterectomy'],
  
  // Radiation
  'radiation': ['radiation', 'prostate cancer', 'side effects', 'treatment'],
  
  // Other
  'hematuria': ['hematuria', 'blood in urine', 'cystoscopy'],
  'urodynamics': ['urodynamics', 'bladder function', 'incontinence'],
  'retention': ['retention', 'catheter', 'unable to urinate', 'blocked'],
  'frequent urination': ['frequent urination', 'overactive bladder', 'bph', 'urgency'],
  'weak stream': ['weak stream', 'bph', 'prostate', 'urinary flow', 'enlarged prostate'],
  'weak urinary stream': ['weak stream', 'bph', 'prostate', 'urinary flow', 'enlarged prostate'],
  'urinary stream': ['urinary stream', 'bph', 'prostate', 'enlarged prostate'],
  'pyeloplasty': ['pyeloplasty', 'upj', 'kidney', 'obstruction'],
  
  // Balanitis and foreskin conditions
  'balanitis': ['balanitis', 'foreskin', 'phimosis', 'infection', 'hygiene', 'circumcision'],
  'foreskin': ['foreskin', 'balanitis', 'phimosis', 'circumcision'],
  
  // Genital warts / HPV
  'genital warts': ['genital warts', 'HPV', 'STI', 'sexually transmitted', 'warts', 'vaccination'],
  'hpv': ['HPV', 'genital warts', 'STI', 'vaccine', 'Gardasil', 'sexually transmitted'],
  'warts': ['genital warts', 'HPV', 'STI', 'cryotherapy'],
  
  // GSM / Menopause / Women's pelvic health
  'gsm': ['GSM', 'menopause', 'vaginal atrophy', 'estrogen', 'women', 'pelvic health'],
  'menopause': ['menopause', 'GSM', 'vaginal health', 'estrogen', 'women', 'urinary symptoms'],
  'vaginal atrophy': ['GSM', 'menopause', 'vaginal dryness', 'estrogen', 'women'],
  'genitourinary syndrome': ['GSM', 'menopause', 'vaginal atrophy', 'women', 'estrogen'],
  
  // Pediatric urology
  'pediatric': ['pediatric', 'children', 'urology', 'birth defects', 'bedwetting'],
  'undescended testicle': ['pediatric', 'children', 'orchiopexy', 'testicle'],
  'hypospadias': ['pediatric', 'children', 'hypospadias', 'birth defects'],
  'bedwetting': ['pediatric', 'children', 'bedwetting', 'enuresis'],
  
  // Pelvic organ prolapse
  'prolapse': ['prolapse', 'cystocele', 'rectocele', 'pelvic floor', 'pessary', 'women'],
  'pelvic organ prolapse': ['prolapse', 'cystocele', 'rectocele', 'pelvic floor', 'pessary', 'women'],
  'cystocele': ['cystocele', 'prolapse', 'bladder', 'pelvic floor', 'women'],
  'rectocele': ['rectocele', 'prolapse', 'pelvic floor', 'women'],
  'pessary': ['pessary', 'prolapse', 'pelvic floor', 'women'],
  
  // Peyronie's disease
  "peyronie's": ['peyronie', 'penile curvature', 'xiaflex', 'plaque', 'erectile dysfunction'],
  'penile curvature': ['peyronie', 'penile curvature', 'xiaflex', 'treatment'],
  'xiaflex': ['xiaflex', 'peyronie', 'injection', 'collagenase']
};

// Extract matching keywords from article title
const getMatchingKeywords = (title) => {
  const lowerTitle = title.toLowerCase();
  let keywords = new Set();
  
  // Check each mapping
  for (const [term, videoKeywords] of Object.entries(articleToVideoKeywords)) {
    if (lowerTitle.includes(term)) {
      videoKeywords.forEach(kw => keywords.add(kw));
    }
  }
  
  // Also extract individual important words from title
  const importantTerms = [
    'ureteroscopy', 'lithotripsy', 'pcnl', 'eswl',
    'prostate', 'bph', 'turp', 'greenlight', 'holep', 'urolift', 'rezum', 'aquablation',
    'bladder', 'cystoscopy', 'turbt', 'bcg', 'cystectomy',
    'kidney', 'nephrectomy', 'renal',
    'testicular', 'testicle', 'orchiectomy', 'varicocele', 'hydrocele', 'vasectomy',
    'incontinence', 'sling', 'sphincter',
    'catheter', 'stent', 'nephrostomy',
    'cancer', 'tumor', 'biopsy',
    'stone', 'calculus',
    'erectile', 'impotence', 'testosterone',
    // New terms for added videos
    'balanitis', 'foreskin', 'phimosis',
    'hpv', 'warts', 'genital',
    'gsm', 'menopause', 'vaginal', 'atrophy',
    'pediatric', 'children', 'hypospadias', 'undescended',
    'prolapse', 'cystocele', 'rectocele', 'pessary', 'pelvic',
    'peyronie', 'curvature', 'xiaflex',
    // Hydrocele and spermatocele terms
    'hydrocele', 'hydrocelectomy', 'spermatocele', 'spermatocelectomy', 'epididymal', 'scrotal'
  ];
  
  importantTerms.forEach(term => {
    if (lowerTitle.includes(term)) {
      keywords.add(term);
    }
  });
  
  return [...keywords];
};

// Score how well a video matches the keywords
const scoreVideoMatch = (video, keywords) => {
  let score = 0;
  const videoTitle = video.title.toLowerCase();
  const videoDesc = (video.description || '').toLowerCase();
  const videoTags = (video.tags || []).map(t => t.toLowerCase());
  const videoCategory = (video.category || '').toLowerCase();
  
  keywords.forEach(keyword => {
    const kw = keyword.toLowerCase();
    // Title match is highest priority
    if (videoTitle.includes(kw)) score += 10;
    // Tag match is high priority
    if (videoTags.some(tag => tag.includes(kw) || kw.includes(tag))) score += 8;
    // Category match
    if (videoCategory.includes(kw)) score += 5;
    // Description match
    if (videoDesc.includes(kw)) score += 3;
  });
  
  return score;
};

const RelatedVideos = ({ articleCategory, articleTitle, articleTags = [], limit = 3 }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadRelatedVideos = async () => {
      try {
        // Get keywords from article title
        const keywords = getMatchingKeywords(articleTitle || '');
        
        // Add article tags
        if (articleTags.length > 0) {
          articleTags.forEach(tag => keywords.push(tag.toLowerCase()));
        }
        
        if (keywords.length === 0) {
          setLoading(false);
          return;
        }
        
        // Fetch all videos and score them
        const response = await axios.get(`${API}/videos?limit=100`);
        const allVideos = response.data;
        
        // Score each video
        const scoredVideos = allVideos.map(video => ({
          ...video,
          matchScore: scoreVideoMatch(video, keywords)
        }));
        
        // Filter videos with score > 0 and sort by score
        const matchedVideos = scoredVideos
          .filter(v => v.matchScore > 0)
          .sort((a, b) => b.matchScore - a.matchScore)
          .slice(0, limit);
        
        setVideos(matchedVideos);
      } catch (error) {
        console.error("Error loading related videos:", error);
      } finally {
        setLoading(false);
      }
    };
    
    loadRelatedVideos();
  }, [articleCategory, articleTitle, articleTags, limit]);

  if (loading) {
    return null;
  }

  if (videos.length === 0) {
    return null;
  }

  return (
    <div className="related-videos-section" data-testid="related-videos">
      <div className="related-videos-header">
        <h3>
          <Video size={20} />
          Related Videos
        </h3>
        <Link to="/videos" className="view-all-link">
          View All Videos
          <ChevronRight size={16} />
        </Link>
      </div>
      
      <div className="related-videos-grid">
        {videos.map(video => (
          <VideoCard key={video.id} video={video} compact={true} />
        ))}
      </div>
    </div>
  );
};

export default RelatedVideos;
